/**
 * Shared form defaults for user creation/editing.
 *
 * The backend (defaults.py) is the single source of truth; these are the values the form
 * shows until GET /api/user-defaults answers, and what it falls back to if that call fails.
 *
 * They had drifted, and the form applied only two of the API's fields over them, so the
 * other three shipped these numbers: cpu_reserved 0.5 against the backend's 0.1 and
 * memory_reserved_gb 0.75 against 0.25. Those are the soft reservations the scheduler
 * bin-packs by, so every console-created user reserved several times what the platform
 * intends and packed a fraction of the agents per node — while a coupon- or API-created
 * user got the right numbers. tests/test_frontend_defaults_match_backend.py now parses both
 * files and fails if they disagree again.
 */

import { apiFetch } from '../../api';

// Must match backend/defaults.py RESOURCE_DEFAULTS (pinned by a test).
export const RESOURCE_DEFAULTS = {
  cpu_reserved: 0.1,
  memory_reserved_gb: 0.25,
  cpu_limit: null,        // null = protected platform ceiling, at least the request
  memory_limit_gb: 1.5,   // hard cap, contains a runaway agent
  memory_swap_gb: 4.0,
};

// Must match backend/defaults.py QUOTA_DEFAULTS (pinned by the same test).
export const QUOTA_DEFAULTS = {
  cron_min_interval: 60,
  cron_max_jobs: 3,
  max_concurrent_subagents: 3,
  exec_mode: 'full',
  browser_engine: 'lightpanda',
  timezone: 'Europe/Rome',
};

export const BROWSER_ENGINES = [
  { value: 'lightpanda', label: 'Lightpanda (default, low memory)' },
  { value: 'chromium', label: 'Chromium (full JavaScript and native screenshots)' },
];

// Cache for API-fetched defaults
let _cachedDefaults = null;

/**
 * Fetch defaults from backend API (cached).
 * Falls back to local constants if API unavailable.
 */
export async function fetchDefaults() {
  if (_cachedDefaults) return _cachedDefaults;
  
  try {
    const resp = await apiFetch('/api/user-defaults');
    if (resp.ok) {
      _cachedDefaults = await resp.json();
      return _cachedDefaults;
    }
  } catch (e) {
    console.warn('Failed to fetch defaults from API, using fallbacks:', e);
  }
  
  // Return local fallbacks
  return {
    ...RESOURCE_DEFAULTS,
    ...QUOTA_DEFAULTS,
  };
}

// `getFormDefaults`, `mergeWithDefaults`, `prepareForApi`, `CHANNEL_DEFAULTS` and
// `IDENTITY_DEFAULTS` used to live here. Nothing in the repo imported any of them — both
// pages build and submit their own form state — and `prepareForApi` in particular encoded
// the empty-means-null convention that the update service reads as "unchanged", so keeping
// it around as an unused helper was an invitation to reintroduce that bug.
